/**
 * Domain enumerations shared across API, web and mobile.
 * Defined as `as const` objects (not TS enums) so they are tree-shakeable,
 * usable in Zod (`z.enum(Object.values(X))`) and serialisable as plain strings.
 * Keep in sync with the Prisma enums in apps/api/prisma/schema.prisma.
 */

export const ListRole = {
  OWNER: 'OWNER',
  EDITOR: 'EDITOR',
  VIEWER: 'VIEWER',
} as const;
export type ListRole = (typeof ListRole)[keyof typeof ListRole];

/** Rank used for "at least" checks: OWNER ⊃ EDITOR ⊃ VIEWER. */
export const LIST_ROLE_RANK: Readonly<Record<ListRole, number>> = {
  VIEWER: 1,
  EDITOR: 2,
  OWNER: 3,
};

export const ListColor = {
  DEFAULT: 'DEFAULT',
  RED: 'RED',
  ORANGE: 'ORANGE',
  YELLOW: 'YELLOW',
  GREEN: 'GREEN',
  TEAL: 'TEAL',
  BLUE: 'BLUE',
  INDIGO: 'INDIGO',
  PURPLE: 'PURPLE',
  PINK: 'PINK',
  BROWN: 'BROWN',
  GRAY: 'GRAY',
} as const;
export type ListColor = (typeof ListColor)[keyof typeof ListColor];

export const Unit = {
  PIECE: 'PIECE',
  KG: 'KG',
  G: 'G',
  LB: 'LB',
  OZ: 'OZ',
  L: 'L',
  ML: 'ML',
  PACK: 'PACK',
  DOZEN: 'DOZEN',
  BOTTLE: 'BOTTLE',
  CAN: 'CAN',
  BOX: 'BOX',
  BAG: 'BAG',
  BUNCH: 'BUNCH',
} as const;
export type Unit = (typeof Unit)[keyof typeof Unit];

export const Locale = { EN: 'en', AR: 'ar' } as const;
export type Locale = (typeof Locale)[keyof typeof Locale];
export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'ar'];
export const RTL_LOCALES: readonly Locale[] = ['ar'];

export const Theme = { LIGHT: 'LIGHT', DARK: 'DARK', SYSTEM: 'SYSTEM' } as const;
export type Theme = (typeof Theme)[keyof typeof Theme];

export const UnitSystem = { METRIC: 'METRIC', IMPERIAL: 'IMPERIAL' } as const;
export type UnitSystem = (typeof UnitSystem)[keyof typeof UnitSystem];

/**
 * Version (publication date) of the Terms and Conditions and Privacy Policy that users accept
 * at registration. Bump it with any material change to `legal.*` in packages/i18n; the
 * accepted version is stored per user.
 */
export const LEGAL_VERSION = '2026-10-19';

export const PlatformRole = { USER: 'USER', ADMIN: 'ADMIN' } as const;
export type PlatformRole = (typeof PlatformRole)[keyof typeof PlatformRole];

export const InvitationStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  REVOKED: 'REVOKED',
  EXPIRED: 'EXPIRED',
} as const;
export type InvitationStatus = (typeof InvitationStatus)[keyof typeof InvitationStatus];

export const ItemRefKind = { CATALOG: 'catalog', USER: 'user' } as const;
export type ItemRefKind = (typeof ItemRefKind)[keyof typeof ItemRefKind];

/** Business limits (domain-model.md). Single source for API and clients. */
/**
 * Stable slug (catalog.json) of "My Items": users' own custom items only. New custom items go
 * there by default; global catalog items may not (ADR-0023).
 */
export const MY_ITEMS_CATEGORY_SLUG = 'my-items';

export const LIMITS = {
  LIST_NAME_MAX: 100,
  LIST_ITEMS_MAX: 500,
  LIST_MEMBERS_MAX: 50,
  ITEM_NOTE_MAX: 500,
  ITEM_NAME_MAX: 120,
  QUANTITY_MAX: 9999,
  QUANTITY_DECIMALS: 3,
  PASSWORD_MIN: 10,
  PASSWORD_MAX: 128,
  DISPLAY_NAME_MAX: 60,
  SEARCH_QUERY_MIN: 2,
  SEARCH_QUERY_MAX: 50,
  SEARCH_PAGE_DEFAULT: 20,
  SEARCH_PAGE_MAX: 50,
  SEARCH_RESULTS_MAX: 200,
  SEARCH_CATEGORY_FILTER_MAX: 10,
  LISTS_PER_USER_MAX: 200,
  CUSTOM_ITEMS_MAX: 2000,
  FAVORITES_MAX: 1000,
  /** Recently used items kept per user (BR-REC-02). */
  RECENT_ITEMS_MAX: 50,
  /** Default page of GET /items/recent. */
  RECENT_ITEMS_DEFAULT: 20,
  /** How many recent items get the relevance boost in search (BR-REC-04). */
  RECENT_ITEMS_SEARCH_BOOST: 20,
  UNDO_WINDOW_SECONDS: 30,
  INVITATION_TTL_DAYS: 14,
  TRASH_RETENTION_DAYS: 30,
  PAGE_SIZE_DEFAULT: 50,
  PAGE_SIZE_MAX: 200,
  BATCH_ADD_MAX: 50,
  /** AI list suggestions: what the user writes, and how many items come back. */
  AI_DESCRIPTION_MAX: 500,
  AI_SUGGESTIONS_MAX: 30,
  AI_EXCLUDE_MAX: 100,
  /**
   * Photo of a paper list (ADR-0022): decoded bytes after the client shrinks it (longest side
   * AI_SCAN_IMAGE_MAX_SIDE px), and how many lines come back (one batch add).
   */
  AI_SCAN_IMAGE_MAX_BYTES: 4_000_000,
  AI_SCAN_IMAGE_MAX_SIDE: 1600,
  AI_SCAN_ITEMS_MAX: 50,
  /** Sponsored lists (admin-managed, targeted by country and city). */
  SHARE_LINKS_MAX: 20,
  SHARE_LINK_DEFAULT_DAYS: 30,
  SHARE_LINK_MAX_DAYS: 365,
  SPONSORED_TITLE_MAX: 80,
  SPONSORED_DESCRIPTION_MAX: 300,
  SPONSORED_ITEMS_MAX: 100,
  SPONSORED_TARGETS_MAX: 200,
  CITY_MAX: 80,
} as const;

/** Stable machine-readable error codes returned in problem+json `code`. */
export const ErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_TAKEN: 'EMAIL_TAKEN',
  RESET_TOKEN_INVALID: 'RESET_TOKEN_INVALID',
  VERIFICATION_TOKEN_INVALID: 'VERIFICATION_TOKEN_INVALID',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  FORBIDDEN: 'FORBIDDEN',
  // Lists
  LIST_NOT_FOUND: 'LIST_NOT_FOUND',
  INSUFFICIENT_LIST_ROLE: 'INSUFFICIENT_LIST_ROLE',
  LIST_ARCHIVED: 'LIST_ARCHIVED',
  LIST_DELETED: 'LIST_DELETED',
  LIST_LIMIT: 'LIST_LIMIT',
  VERSION_MISMATCH: 'VERSION_MISMATCH',
  PRECONDITION_REQUIRED: 'PRECONDITION_REQUIRED',
  // Items
  LIST_ITEM_LIMIT: 'LIST_ITEM_LIMIT',
  QUANTITY_LIMIT: 'QUANTITY_LIMIT',
  STALE_POSITION: 'STALE_POSITION',
  UNDO_EXPIRED: 'UNDO_EXPIRED',
  CUSTOM_ITEM_LIMIT: 'CUSTOM_ITEM_LIMIT',
  CUSTOM_ITEM_EXISTS: 'CUSTOM_ITEM_EXISTS',
  CUSTOM_ITEM_IN_USE: 'CUSTOM_ITEM_IN_USE',
  FAVORITE_LIMIT: 'FAVORITE_LIMIT',
  POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
  // Sharing
  LIST_MEMBER_LIMIT: 'LIST_MEMBER_LIMIT',
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  ALREADY_INVITED: 'ALREADY_INVITED',
  CANNOT_SHARE_WITH_SELF: 'CANNOT_SHARE_WITH_SELF',
  CANNOT_CHANGE_OWNER_ROLE: 'CANNOT_CHANGE_OWNER_ROLE',
  CANNOT_REMOVE_OWNER: 'CANNOT_REMOVE_OWNER',
  OWNER_CANNOT_LEAVE: 'OWNER_CANNOT_LEAVE',
  MEMBER_NOT_FOUND: 'MEMBER_NOT_FOUND',
  SHARE_NOT_FOUND: 'SHARE_NOT_FOUND',
  // Meals
  CUISINE_NOT_FOUND: 'CUISINE_NOT_FOUND',
  MEAL_NOT_FOUND: 'MEAL_NOT_FOUND',
  // AI suggestions / sponsored lists
  AI_UNAVAILABLE: 'AI_UNAVAILABLE',
  SPONSORED_LIST_NOT_FOUND: 'SPONSORED_LIST_NOT_FOUND',
  // Share links
  SHARE_LINK_NOT_FOUND: 'SHARE_LINK_NOT_FOUND',
  SHARE_LINK_LIMIT: 'SHARE_LINK_LIMIT',
  INVITATION_INVALID: 'INVITATION_INVALID',
  INVITATION_EMAIL_MISMATCH: 'INVITATION_EMAIL_MISMATCH',
  // Platform
  RATE_LIMITED: 'RATE_LIMITED',
  PDF_UNAVAILABLE: 'PDF_UNAVAILABLE',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/** RFC 9457 problem details as returned by the API. */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code: ErrorCode;
  detail?: string;
  instance?: string;
  requestId?: string;
  errors?: Array<{ path: string; code: string; message: string }>;
}

/** Cursor pagination envelope. */
export interface Page<T> {
  data: T[];
  page: { nextCursor: string | null; hasMore: boolean };
}

/** How often the user needs the list; steers the AI's quantities and item mix. */
export const SuggestionFrequency = {
  ONE_TIME: 'ONE_TIME',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
} as const;
export type SuggestionFrequency = (typeof SuggestionFrequency)[keyof typeof SuggestionFrequency];

/** Lifecycle of an admin-managed sponsored list; only PUBLISHED ones reach users. */
export const SponsoredStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type SponsoredStatus = (typeof SponsoredStatus)[keyof typeof SponsoredStatus];

/** ISO 3166-1 alpha-2 codes. Names come from `Intl.DisplayNames` in the user's language. */
export const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
  'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP ' +
  'KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT ' +
  'MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
  'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG ' +
  'UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');
